import React, { useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { MobileCardSkeleton } from './mobile-card-skeleton';
import styles from './mobile-infinite-virtual-list.module.css';

type Props<TItem> = {
  data: TItem[];
  total: number;
  isLoadingInitial: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  renderItem: (item: TItem, index: number) => React.ReactNode;
  empty?: React.ReactNode;
  skeletonCount?: number;
  itemGap?: number;
  height?: number | string;
  viewportOffset?: number;
  paddingX?: number;
  paddingY?: number;
};

export function MobileInfiniteVirtualList<TItem>({
  data,
  total,
  isLoadingInitial,
  isLoadingMore,
  loadMore,
  renderItem,
  empty,
  skeletonCount = 9,
  itemGap = 22,
  height,
  viewportOffset = 170,
  paddingX = 16,
  paddingY = 16,
}: Props<TItem>) {
  const handleEndReached = useCallback(() => {
    if (isLoadingInitial || isLoadingMore) {
      return;
    }

    if (data.length >= total) {
      return;
    }

    loadMore();
  }, [data.length, isLoadingInitial, isLoadingMore, loadMore, total]);

  const resolvedHeight = height ?? `calc(100dvh - ${viewportOffset}px)`;

  const containerStyle: React.CSSProperties = {
    height: resolvedHeight,
    width: '100%',
    backgroundColor: 'transparent',
  };

  if (isLoadingInitial) {
    return (
      <div className={styles.container} style={{ ...containerStyle, overflowY: 'auto' }}>
        <div className={styles.inset} style={{ padding: `${paddingY}px ${paddingX}px` }}>
          <MobileCardSkeleton count={skeletonCount} gap={itemGap} mode="initial" />
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={styles.container} style={{ ...containerStyle, overflowY: 'auto' }}>
        <div className={styles.inset} style={{ padding: `${paddingY}px ${paddingX}px` }}>
          {empty ?? null}
        </div>
      </div>
    );
  }

  return (
    <Virtuoso
      className={styles.virtuoso}
      style={containerStyle}
      data={data}
      endReached={handleEndReached}
      itemContent={(index, item) => {
        const isFirst = index === 0;
        const isLast = index === data.length - 1;
        const paddingTop = isFirst ? paddingY : 0;
        const paddingBottom = isLast ? (isLoadingMore ? itemGap : paddingY) : itemGap;

        return (
          <div
            className={styles.item}
            style={{
              paddingLeft: paddingX,
              paddingRight: paddingX,
              paddingTop,
              paddingBottom,
            }}
          >
            {renderItem(item, index)}
          </div>
        );
      }}
      components={{
        Footer: () =>
          isLoadingMore ? (
            <div
              className={styles.footer}
              style={{ paddingLeft: paddingX, paddingRight: paddingX, paddingBottom: paddingY }}
            >
              <MobileCardSkeleton count={1} gap={itemGap} mode="append" />
            </div>
          ) : null,
      }}
    />
  );
}
